import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import { useCart } from '../../context/CartContext';
import ProductCard from './ProductCard';
import ProductOptionsModal from './ProductOptionsModal';
import OfflineService from '../../services/OfflineService';
import logo from '../../assets/logo-danes.png';

const ITEMS_PER_PAGE = 12;
const EXCLUDED_CATEGORIES = ['All', 'Expenses', 'Saleable', 'PoS'];

const ProductList = () => {
    const { categories, sessionData, settings } = useAppContext();
    const { addItem } = useCart();
    const navigate = useNavigate();
    const { categoryId } = useParams();

    const [filteredProducts, setFilteredProducts] = useState([]);
    const [displayProducts, setDisplayProducts] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [isOffline, setIsOffline] = useState(!navigator.onLine);

    useEffect(() => {
        if (!sessionData || !sessionData.authenticated) {
            navigate('/login', { replace: true });
        }
    }, [sessionData, navigate]);

    useEffect(() => {
        const handleOnline = () => setIsOffline(false);
        const handleOffline = () => setIsOffline(true);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    const getFilteredCategories = useCallback(() => {
        if (!categories || !Array.isArray(categories)) {
            return [];
        }

        return categories.filter(category => {
            const nameMatch = !EXCLUDED_CATEGORIES.some(excluded =>
                category.name && category.name.toLowerCase().includes(excluded.toLowerCase())
            );

            const idMatch = !EXCLUDED_CATEGORIES.some(excluded =>
                category.id && category.id.toString().toLowerCase().includes(excluded.toLowerCase())
            );

            return nameMatch && idMatch;
        });
    }, [categories]);

    const products = useMemo(() => {
        let loadedProducts = [];

        if (sessionData && sessionData.products && Array.isArray(sessionData.products)) {
            loadedProducts = sessionData.products.filter(product => product.is_totem);
        } else if (isOffline) {
            loadedProducts = OfflineService.getProducts().filter(product => product.is_totem);
        } else {
            loadedProducts = [];
        }

        return loadedProducts;
    }, [sessionData, isOffline]);

    const filterProducts = useCallback(() => {
        setLoading(true);

        try {
            const filtered = products.filter(product => {
                return categoryId === 'all' || product.category_id === parseInt(categoryId);
            });

            setFilteredProducts(filtered);
            setTotalPages(Math.ceil(filtered.length / ITEMS_PER_PAGE));
            setCurrentPage(1);
        } catch (error) {
            setFilteredProducts([]);
            setTotalPages(1);
        } finally {
            setLoading(false);
        }
    }, [categoryId, products]);

    useEffect(() => {
        filterProducts();
    }, [filterProducts]);

    useEffect(() => {
        const start = (currentPage - 1) * ITEMS_PER_PAGE;
        const end = start + ITEMS_PER_PAGE;
        setDisplayProducts(filteredProducts.slice(start, end));
    }, [currentPage, filteredProducts]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
        document.querySelector('.products-grid')?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleAddToCart = (product) => {
        if (product.custom_attributes && product.custom_attributes.length > 0) {
            setSelectedProduct(product);
        } else {
            addItem(product);
        }
    };

    const handleModalSubmit = (selectedQuantities, totalPrice, attributesDescription, attribute_lines, nested_attributes) => {
        addItem({
            ...selectedProduct,
            price: totalPrice,
            customDescription: attributesDescription,
            selectedAttributes: selectedQuantities,
            attribute_lines: attribute_lines || [],
            nested_attributes: nested_attributes || null,
            id: `${selectedProduct.id}-custom-${Date.now()}`,
            hasOptions: true
        });
        setSelectedProduct(null);
    };

    const handleModalClose = () => {
        setSelectedProduct(null);
    };

    const handleBackToMenu = () => {
        navigate('/');
    };

    if (!sessionData || !sessionData.authenticated) {
        return null;
    }

    const currentCategory = categoryId === 'all'
        ? { name: 'Todos los productos' }
        : getFilteredCategories().find(cat => cat.id === parseInt(categoryId)) || { name: 'Productos' };

    return (
        <div className="flex flex-col h-full animate-slide-in">
            <div
                className="py-6 shadow-xl relative overflow-hidden"
                style={{ backgroundColor: settings.card_color }}
            >
                <div className="relative z-10 flex flex-col items-center mb-4">
                    <img
                        src={logo}
                        alt="Danés"
                        className="h-20 w-auto animate-fade-in"
                    />
                </div>

                <div className="px-6">
                    <div className="flex items-center justify-between mb-4">
                        <button
                            onClick={handleBackToMenu}
                            className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-white transition-all duration-300 hover:scale-105 shadow-lg"
                            style={{ backgroundColor: settings.button_color }}
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                            Volver al Menú
                        </button>
                    </div>

                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">{currentCategory.name}</h1>
                        <p className="text-gray-600">
                            Descubre nuestros deliciosos productos. Toca el botón "+" para agregar al carrito.
                        </p>
                    </div>

                    {!loading && (
                        <div className="flex justify-between items-center text-sm text-gray-600 mt-4">
                            <span>
                                Mostrando {displayProducts.length} de {filteredProducts.length} productos
                            </span>
                            {totalPages > 1 && (
                                <span>
                                    Página {currentPage} de {totalPages}
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <div
                className="flex-1 overflow-auto p-6"
                style={{ backgroundColor: settings.box_color }}
            >
                {loading ? (
                    <div className="flex flex-col items-center justify-center h-64">
                        <div className="animate-spin inline-block w-12 h-12 border-4 border-current border-t-transparent rounded-full text-blue-600"></div>
                        <p className="mt-4 text-gray-600 text-lg">Cargando productos...</p>
                    </div>
                ) : products.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-64 text-center">
                        <svg className="w-20 h-20 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                        </svg>
                        <h3 className="text-xl font-medium text-gray-900 mb-2">
                            {isOffline ? 'No hay productos disponibles en modo offline' : 'No se encontraron productos'}
                        </h3>
                        <p className="text-gray-600">
                            {isOffline
                                ? 'Conéctate para cargar los productos desde el servidor.'
                                : 'Intenta seleccionar otra categoría.'}
                        </p>
                    </div>
                ) : filteredProducts.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-64 text-center">
                        <svg className="w-20 h-20 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <h3 className="text-xl font-medium text-gray-900 mb-2">No se encontraron productos</h3>
                    </div>
                ) : (
                    <>
                        <div className="products-grid">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
                                {displayProducts.map((product, index) => (
                                    <div
                                        key={product.id}
                                        className="animate-fade-in-up"
                                        style={{ animationDelay: `${index * 0.1}s` }}
                                    >
                                        <ProductCard
                                            product={product}
                                            onAddToCart={handleAddToCart}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>

                        {totalPages > 1 && (
                            <div className="mt-12 flex justify-center">
                                <nav className="flex items-center space-x-2">
                                    <button
                                        onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                                        disabled={currentPage === 1}
                                        className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 font-medium"
                                    >
                                        Anterior
                                    </button>

                                    <div className="flex items-center space-x-1">
                                        {[...Array(Math.min(5, totalPages))].map((_, i) => {
                                            let pageNum;
                                            if (totalPages <= 5) {
                                                pageNum = i + 1;
                                            } else if (currentPage <= 3) {
                                                pageNum = i + 1;
                                            } else if (currentPage >= totalPages - 2) {
                                                pageNum = totalPages - 4 + i;
                                            } else {
                                                pageNum = currentPage - 2 + i;
                                            }

                                            return (
                                                <button
                                                    key={pageNum}
                                                    onClick={() => handlePageChange(pageNum)}
                                                    className={`w-10 h-10 flex items-center justify-center rounded-lg transition-all duration-200 font-medium ${currentPage === pageNum
                                                        ? 'text-white shadow-lg transform scale-105'
                                                        : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                                                        }`}
                                                    style={{
                                                        backgroundColor: currentPage === pageNum ? settings.button_color : 'transparent'
                                                    }}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        })}

                                        {totalPages > 5 && currentPage < totalPages - 2 && (
                                            <>
                                                <span className="px-2 text-gray-500">...</span>
                                                <button
                                                    onClick={() => handlePageChange(totalPages)}
                                                    className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors duration-200 font-medium"
                                                >
                                                    {totalPages}
                                                </button>
                                            </>
                                        )}
                                    </div>

                                    <button
                                        onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                                        disabled={currentPage === totalPages}
                                        className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 font-medium"
                                    >
                                        Siguiente
                                    </button>
                                </nav>
                            </div>
                        )}
                    </>
                )}
            </div>

            {selectedProduct && (
                <ProductOptionsModal
                    product={selectedProduct}
                    onClose={handleModalClose}
                    onSubmit={handleModalSubmit}
                />
            )}

            <style jsx>{`
                @keyframes fade-in-up {
                    0% {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    100% {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                @keyframes slide-in {
                    0% {
                        opacity: 0;
                        transform: translateX(-20px);
                    }
                    100% {
                        opacity: 1;
                        transform: translateX(0);
                    }
                }

                @keyframes fade-in {
                    from {
                        opacity: 0;
                        transform: translateY(-20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .animate-fade-in {
                    animation: fade-in 0.8s ease-out;
                }

                .animate-fade-in-up {
                    animation: fade-in-up 0.6s ease-out both;
                }

                .animate-slide-in {
                    animation: slide-in 0.5s ease-out;
                }

                ::-webkit-scrollbar {
                    width: 6px;
                }

                ::-webkit-scrollbar-track {
                    background: #f1f1f1;
                    border-radius: 3px;
                }

                ::-webkit-scrollbar-thumb {
                    background: #c1c1c1;
                    border-radius: 3px;
                }

                ::-webkit-scrollbar-thumb:hover {
                    background: #a1a1a1;
                }
            `}</style>
        </div>
    );
};

export default ProductList;